/**
 * API client para o app `comercial/` do backend (Fase 3 do PharuERP).
 *
 * Padrao: cada funcao recebe `request` (de `useAuth()`) que faz o refresh
 * de JWT automatico. Endpoints sob `/api/comercial/`. URLs sem trailing
 * slash (convenção do projeto, `APPEND_SLASH=False`).
 */

const PREFIXO_CLIENTE = "/api/comercial/clientes";
const PREFIXO_REPRESENTANTE = "/api/comercial/representantes";

// ---------- Clientes ----------

/**
 * Lista clientes. Backend: DRF SearchFilter + filtros custom.
 *
 * Params suportados:
 *   busca           -> 'search' (texto livre em nome/doc/email/cidade)
 *   tipo            -> 'PF' | 'PJ'
 *   incluirInativos -> 'incluir_inativos=true' (default: so ativos)
 *   ativo           -> 'ativo=true|false' (false = SO os inativos, CRM-10)
 *   bloqueado       -> true|false
 *   ordering        -> 'nome' | '-nome' | 'created_at' | '-created_at'
 *   page, pageSize  -> paginacao DRF
 */
function _filtrosClienteParaQuery(params = {}) {
  const usp = new URLSearchParams();
  if (params.busca) usp.set("search", params.busca);
  if (params.tipo) usp.set("tipo", params.tipo);
  if (params.incluirInativos) usp.set("incluir_inativos", "true");
  if (params.ativo !== undefined && params.ativo !== null) {
    usp.set("ativo", params.ativo ? "true" : "false");
  }
  if (params.bloqueado !== undefined && params.bloqueado !== null) {
    usp.set("bloqueado", params.bloqueado ? "true" : "false");
  }
  return usp;
}

export function listarClientes(request, params = {}) {
  const usp = _filtrosClienteParaQuery(params);
  // Aba de saude do painel: saudaveis | atencao | em_risco (filtra por tier).
  if (params.saude) usp.set("saude", params.saude);
  if (params.ordering) usp.set("ordering", params.ordering);
  if (params.page) usp.set("page", params.page);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREFIXO_CLIENTE}${qs ? `?${qs}` : ""}`);
}

/**
 * KPIs do painel de saude de clientes (contagem/% por tier, score medio,
 * LTV medio, ativos, em risco, total LTV do Top 10). Respeita os mesmos
 * filtros busca/tipo/status da lista — NAO a aba de saude.
 */
export function obterSaudeResumoClientes(request, params = {}) {
  const usp = _filtrosClienteParaQuery(params);
  const qs = usp.toString();
  return request(`${PREFIXO_CLIENTE}/saude-resumo${qs ? `?${qs}` : ""}`);
}

export function obterCliente(request, id, options = {}) {
  // options pode incluir { silentError: true } pra desabilitar auto-toast
  // global quando a tela trata erro inline (ex.: 404 com mensagem custom).
  return request(`${PREFIXO_CLIENTE}/${id}`, options);
}

/**
 * Cockpit do cliente (tela Detalhe) — payload unico: KPIs + saude/breakdown
 * + pedidos&ambientes + atendimentos + notas + parceria. Backend:
 * GET /api/comercial/clientes/{id}/cockpit.
 */
export function obterCockpitCliente(request, id, options = {}) {
  return request(`${PREFIXO_CLIENTE}/${id}/cockpit`, options);
}

/**
 * Resumo financeiro do cliente (aba Financeiro do Cliente 360): total
 * contratado/recebido/a-receber/vencido + inadimplencia + parcelas (boletos)
 * + limite de credito + condicoes. Backend: GET /clientes/{id}/financeiro.
 */
export function obterFinanceiroCliente(request, id, options = {}) {
  return request(`${PREFIXO_CLIENTE}/${id}/financeiro`, options);
}

/**
 * Documentos do cliente (aba Documentos do Cliente 360): agregador read-only
 * de NF-e + contratos + anexos de orcamento/lead. GET /clientes/{id}/documentos.
 */
export function obterDocumentosCliente(request, id, options = {}) {
  return request(`${PREFIXO_CLIENTE}/${id}/documentos`, options);
}

/**
 * Detalhe de 1 ambiente do pedido (drill-down do cockpit): BOM CAD
 * (chapas/bordas/ferragens do DinaBox) + status de producao das pecas (OPs).
 * Backend: GET /api/comercial/ambientes/{id}.
 */
export function obterDetalheAmbiente(request, ambienteId, options = {}) {
  return request(`/api/comercial/ambientes/${ambienteId}`, options);
}

// `options` repassa ao `request` (ex.: `{ silentError: true }` quando o form
// mostra o erro por campo e o toast automatico so repetiria o aviso).
export function criarCliente(request, dados, options = {}) {
  return request(PREFIXO_CLIENTE, {
    method: "POST",
    body: JSON.stringify(dados),
    ...options,
  });
}

export function atualizarCliente(request, id, dados, options = {}) {
  return request(`${PREFIXO_CLIENTE}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
    ...options,
  });
}

export function desativarCliente(request, id) {
  return request(`${PREFIXO_CLIENTE}/${id}/desativar`, { method: "POST" });
}

export function reativarCliente(request, id) {
  return request(`${PREFIXO_CLIENTE}/${id}/reativar`, { method: "POST" });
}

export function bloquearCliente(request, id, motivo) {
  return request(`${PREFIXO_CLIENTE}/${id}/bloquear`, {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

export function desbloquearCliente(request, id) {
  return request(`${PREFIXO_CLIENTE}/${id}/desbloquear`, { method: "POST" });
}

export function anonimizarCliente(request, id) {
  return request(`${PREFIXO_CLIENTE}/${id}/anonimizar`, { method: "POST" });
}

// ---------- Historico CRM herdado pelo cliente (read-only) ----------

// Devolve {leads: [...detalhe completo + timeline], total}. Cada lead que virou
// este cliente, com timeline/anexos/notas/SPIN/tarefas/compromissos/orcamentos.
// Nada e copiado — leitura via FK Lead.cliente.
export function obterHistoricoCrmCliente(request, clienteId, options = {}) {
  return request(`${PREFIXO_CLIENTE}/${clienteId}/historico-crm`, options);
}

// ---------- Ambientes manuais do cliente (com permissao gerir_ambientes) ----

const PREFIXO_AMBIENTE_CLIENTE = "/api/comercial/ambientes-cliente";

export function listarAmbientesCliente(request, clienteId, options = {}) {
  return request(`${PREFIXO_AMBIENTE_CLIENTE}?cliente=${clienteId}`, options);
}

export function criarAmbienteCliente(request, dados) {
  return request(PREFIXO_AMBIENTE_CLIENTE, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarAmbienteCliente(request, id, dados) {
  return request(`${PREFIXO_AMBIENTE_CLIENTE}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function excluirAmbienteCliente(request, id) {
  return request(`${PREFIXO_AMBIENTE_CLIENTE}/${id}`, { method: "DELETE" });
}

// ---------- Pedidos importados (historico, via planilha) ----------

const PREFIXO_PEDIDO_IMPORTADO = "/api/comercial/pedidos-importados";

export function listarPedidosImportados(request, clienteId, options = {}) {
  return request(`${PREFIXO_PEDIDO_IMPORTADO}?cliente=${clienteId}`, options);
}

// ---------- Notas internas do cliente (cockpit) ----------

const PREFIXO_CLIENTE_NOTA = "/api/comercial/cliente-notas";

export function listarNotasCliente(request, clienteId) {
  return request(`${PREFIXO_CLIENTE_NOTA}?cliente=${clienteId}`);
}

export function criarNotaCliente(request, clienteId, texto) {
  return request(PREFIXO_CLIENTE_NOTA, {
    method: "POST",
    body: JSON.stringify({ cliente: clienteId, texto }),
  });
}

export function excluirNotaCliente(request, notaId, options = {}) {
  return request(`${PREFIXO_CLIENTE_NOTA}/${notaId}`, {
    method: "DELETE",
    ...options,
  });
}

// ---------- Representantes ----------

// `options` repassa ao `request` (ex.: `{ silentError: true }` quando a lista
// so alimenta um select e a tela avisa a falha inline). Varias telas ja
// passavam esse 3o argumento, que era descartado aqui.
export function listarRepresentantes(request, params = {}, options = {}) {
  const usp = new URLSearchParams();
  if (params.busca) usp.set("search", params.busca);
  if (params.tipo) usp.set("tipo", params.tipo);
  if (params.incluirInativos) usp.set("incluir_inativos", "true");
  if (params.page) usp.set("page", params.page);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREFIXO_REPRESENTANTE}${qs ? `?${qs}` : ""}`, options);
}

export function obterRepresentante(request, id) {
  return request(`${PREFIXO_REPRESENTANTE}/${id}`);
}

/**
 * Cockpit do parceiro (tela Detalhe) — payload unico: cabecalho + KPIs
 * (RT/indicacoes/volume/comissao/conversao) + proxima acao + indicacoes&pedidos
 * + dados cadastrais + pagamento. Backend:
 * GET /api/comercial/representantes/{id}/cockpit.
 */
export function obterCockpitParceiro(request, id, options = {}) {
  return request(`${PREFIXO_REPRESENTANTE}/${id}/cockpit`, options);
}

export function criarRepresentante(request, dados) {
  return request(PREFIXO_REPRESENTANTE, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarRepresentante(request, id, dados) {
  return request(`${PREFIXO_REPRESENTANTE}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}
